import React, { useEffect, useRef } from 'react';

// 공통 필드 행: 좌측 라벨, 우측 컨트롤(한 줄) + 우측 액션 슬롯
export const FieldRow = ({ label, children, rightAction = null }) => (
  <div className="flex items-center py-2 px-3 hover:bg-gray-50 transition-colors">
    <div className="w-36 shrink-0 text-sm font-medium text-gray-600">{label}</div>
    <div className="flex-1">{children}</div>
    {rightAction && <div className="ml-2">{rightAction}</div>}
  </div>
);

// 인라인 아이콘 버튼 (연필 아이콘)
export const IconButton = ({ ariaLabel, onClick }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-8.486 8.486a2 2 0 01-.878.516l-3.09.772a.5.5 0 01-.606-.606l.772-3.09a2 2 0 01.516-.878l8.486-8.486z" />
      <path d="M12 5l3 3" />
    </svg>
  </button>
);

// 토글 스위치 컴포넌트
export const Switch = ({ checked, onChange, onLabel = 'ON', offLabel = 'OFF', disabled = false }) => {
  const toggle = () => {
    if (disabled) return;
    onChange(!checked);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label="toggle"
      disabled={disabled}
      onClick={toggle}
      onKeyDown={onKeyDown}
      className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${checked ? 'bg-blue-600' : 'bg-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${checked ? 'translate-x-5' : 'translate-x-1'}`}
      />
      <span className="sr-only">{checked ? onLabel : offLabel}</span>
    </button>
  );
};

export const InputField = (props) => (
  <input {...props} className="block w-full px-3 py-2 bg-white placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
);

export const DropdownField = ({ name, value, options, isOpen, onToggle, onChange }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!isOpen) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onToggle(); // close
      }
    };
    const onDocKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onToggle();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [isOpen, onToggle]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={onToggle}
        className="flex justify-between items-center w-full px-3 py-2 bg-white text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value || '선택하세요'}</span>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => onChange(option)}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-100 ${value === option ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}`}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
